/**
 * SparkleParticleSystem
 *
 * Renders animated sparkling particles in dark regions of the position map
 * to indicate that processing is happening. Particles spawn in areas where
 * the B channel (after mirroring) is low, indicating no point cloud data.
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';

interface Particle {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  life: number;
  maxLife: number;
  size: number;
  brightness: number;
  active: boolean;
}

export class SparkleParticleSystem {
  private viewer: Viewer;
  private coordinateTransformer: CoordinateTransformer;

  // Particle system state
  private particles: Particle[] = [];
  private particlePoints: THREE.Points | null = null;
  private particleGeometry: THREE.BufferGeometry | null = null;
  private particleMaterial: THREE.PointsMaterial | null = null;
  private particleContainer: THREE.Group | null = null;

  // Animation state
  private isActive = false;
  private isSpawning = false;
  private lastUpdateTime = 0;
  private animationFrameId: number | null = null;
  private boundUpdate: () => void;

  // Spawn regions (dark pixels from position map)
  private spawnPositions: THREE.Vector3[] = [];
  private positionMapWidth = 0;
  private positionMapHeight = 0;

  // Configuration
  private readonly MAX_PARTICLES = 500;
  private readonly SPAWN_RATE = 50; // particles per second
  private readonly PARTICLE_LIFETIME_MIN = 0.5; // seconds
  private readonly PARTICLE_LIFETIME_MAX = 1.5; // seconds
  private readonly PARTICLE_SIZE_MIN = 3;
  private readonly PARTICLE_SIZE_MAX = 8;
  private readonly DARK_THRESHOLD = 110; // B channel value below which we spawn particles

  constructor(viewer: Viewer, coordinateTransformer: CoordinateTransformer) {
    this.viewer = viewer;
    this.coordinateTransformer = coordinateTransformer;
    this.boundUpdate = this.update.bind(this);
  }

  /**
   * Start the particle system with the given position map.
   * Extracts dark regions from the B channel to use as spawn positions.
   * Only updates spawn positions - rendering is initialized once and keeps running.
   */
  start(positionMap: ImageData): void {
    // Update spawn positions
    this.extractSpawnPositions(positionMap);

    if (this.spawnPositions.length === 0) {
      return; // No dark regions to spawn particles in
    }

    // Enable spawning
    this.isSpawning = true;

    // If already initialized and running, nothing more to do
    if (this.particleGeometry && this.particleContainer) {
      // Make sure update loop is running
      if (!this.isActive) {
        this.isActive = true;
        this.lastUpdateTime = performance.now();
        this.scheduleUpdate();
      }
      return;
    }

    // First time initialization
    this.isActive = true;
    this.lastUpdateTime = performance.now();

    if (!this.initializeRendering()) {
      // Initialization failed (no valid corners) - reset state
      this.isActive = false;
      this.isSpawning = false;
      return;
    }

    this.scheduleUpdate();
  }

  /**
   * Stop spawning new particles. Existing particles will fade out naturally.
   */
  stop(): void {
    this.isSpawning = false;
    this.spawnPositions = [];
  }

  /**
   * Fully stop the particle system and clean up all resources.
   */
  dispose(): void {
    this.isActive = false;
    this.isSpawning = false;

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.cleanup();
    this.particles = [];
    this.spawnPositions = [];
  }

  /**
   * Check if the particle system is currently active (rendering particles).
   */
  getIsActive(): boolean {
    return this.isActive;
  }

  /**
   * Check if the particle system is currently spawning new particles.
   */
  getIsSpawning(): boolean {
    return this.isSpawning;
  }

  /**
   * Check if there are any active particles currently being rendered.
   */
  hasActiveParticles(): boolean {
    return this.particles.some((p) => p.active);
  }

  /**
   * Extract spawn positions from the position map's dark B channel regions.
   * After mirroring, low B values indicate empty space (no point cloud data).
   */
  private extractSpawnPositions(positionMap: ImageData): void {
    this.spawnPositions = [];
    this.positionMapWidth = positionMap.width;
    this.positionMapHeight = positionMap.height;

    const data = positionMap.data;
    const sampleStep = 8; // Sample every Nth pixel to reduce computation

    for (let y = 0; y < positionMap.height; y += sampleStep) {
      for (let x = 0; x < positionMap.width; x += sampleStep) {
        const i = (y * positionMap.width + x) * 4;

        let b = data[i + 2];

        // Apply mirroring to B channel (same as debug widget)
        if (b > 0 && b < 128) {
          b = 255 - b;
        }

        // Check if this is a dark region (low B value after mirroring)
        if (b === 0) {
          // Convert pixel position to world position
          const worldPos = this.coordinateTransformer.pixelToWorld(x, y);
          if (worldPos) {
            this.spawnPositions.push(worldPos);
          }
        }
      }
    }
  }

  /**
   * Initialize Three.js objects for rendering particles.
   * Returns true if initialization succeeded, false if it failed (e.g., no valid corners).
   */
  private initializeRendering(): boolean {
    this.cleanup();

    const maskCorners = this.coordinateTransformer.getMaskCorners();
    if (maskCorners.length !== 4) return false;

    // Use center of workspace as container origin to prevent jitter
    const center = new THREE.Vector3().add(maskCorners[0]).add(maskCorners[1]).add(maskCorners[2]).add(maskCorners[3]).multiplyScalar(0.25);

    this.particleContainer = new THREE.Group();
    this.particleContainer.position.copy(center);

    // Create geometry with max particles
    this.particleGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this.MAX_PARTICLES * 3);
    const sizes = new Float32Array(this.MAX_PARTICLES);
    const colors = new Float32Array(this.MAX_PARTICLES * 3);

    this.particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.particleGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    this.particleGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    // Create material with custom vertex colors and sizes
    this.particleMaterial = new THREE.PointsMaterial({
      size: 6,
      sizeAttenuation: false,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    this.particlePoints = new THREE.Points(this.particleGeometry, this.particleMaterial);
    this.particlePoints.renderOrder = 1100; // Above everything else
    this.particlePoints.frustumCulled = false;

    this.particleContainer.add(this.particlePoints);
    this.viewer.scene.scene.add(this.particleContainer);

    return true;
  }

  /**
   * Clean up Three.js objects.
   */
  private cleanup(): void {
    if (this.particleContainer) {
      this.viewer.scene.scene.remove(this.particleContainer);
    }

    if (this.particleGeometry) {
      this.particleGeometry.dispose();
      this.particleGeometry = null;
    }

    if (this.particleMaterial) {
      this.particleMaterial.dispose();
      this.particleMaterial = null;
    }

    this.particlePoints = null;
    this.particleContainer = null;
  }

  /**
   * Schedule the next update frame.
   */
  private scheduleUpdate(): void {
    if (!this.isActive) return;
    this.animationFrameId = requestAnimationFrame(this.boundUpdate);
  }

  /**
   * Main update loop - spawns new particles, updates existing ones, and renders.
   */
  private update(): void {
    if (!this.isActive || !this.particleGeometry || !this.particleContainer) {
      return;
    }

    const now = performance.now();
    const deltaTime = (now - this.lastUpdateTime) / 1000; // Convert to seconds
    this.lastUpdateTime = now;

    // Spawn new particles
    this.spawnParticles(deltaTime);

    // Update existing particles
    this.updateParticles(deltaTime);

    // Update geometry buffers
    this.updateGeometry();

    // Schedule next frame - loop keeps running until dispose() is called
    this.scheduleUpdate();
  }

  /**
   * Spawn new particles based on spawn rate.
   */
  private spawnParticles(deltaTime: number): void {
    if (!this.isSpawning || this.spawnPositions.length === 0) return;

    const particlesToSpawn = Math.floor(this.SPAWN_RATE * deltaTime);

    for (let i = 0; i < particlesToSpawn; i++) {
      // Find an inactive particle to reuse, or create a new one if under limit
      let particle = this.particles.find((p) => !p.active);

      if (!particle && this.particles.length < this.MAX_PARTICLES) {
        particle = {
          position: new THREE.Vector3(),
          velocity: new THREE.Vector3(),
          life: 0,
          maxLife: 0,
          size: 0,
          brightness: 0,
          active: false,
        };
        this.particles.push(particle);
      }

      if (!particle) break; // No available slots

      // Pick random spawn position
      const spawnIndex = Math.floor(Math.random() * this.spawnPositions.length);
      const basePos = this.spawnPositions[spawnIndex];

      // Reset particle with new values
      const maxLife = this.PARTICLE_LIFETIME_MIN + Math.random() * (this.PARTICLE_LIFETIME_MAX - this.PARTICLE_LIFETIME_MIN);

      particle.position.copy(basePos).add(new THREE.Vector3((Math.random() - 0.5) * 0.1, (Math.random() - 0.5) * 0.1, (Math.random() - 0.5) * 0.05));
      particle.velocity.set((Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.3, Math.random() * 0.2 + 0.1);
      particle.life = maxLife;
      particle.maxLife = maxLife;
      particle.size = this.PARTICLE_SIZE_MIN + Math.random() * (this.PARTICLE_SIZE_MAX - this.PARTICLE_SIZE_MIN);
      particle.brightness = 0.5 + Math.random() * 0.5;
      particle.active = true;
    }
  }

  /**
   * Update particle positions and lifetimes.
   */
  private updateParticles(deltaTime: number): void {
    for (const particle of this.particles) {
      if (!particle.active) continue;

      // Update position
      particle.position.add(particle.velocity.clone().multiplyScalar(deltaTime));

      // Update lifetime
      particle.life -= deltaTime;

      // Add slight random movement for sparkle effect
      particle.velocity.add(new THREE.Vector3((Math.random() - 0.5) * 0.5 * deltaTime, (Math.random() - 0.5) * 0.5 * deltaTime, 0));

      // Mark dead particles as inactive (will be reused)
      if (particle.life <= 0) {
        particle.active = false;
      }
    }
  }

  /**
   * Update the geometry buffers with current particle data.
   */
  private updateGeometry(): void {
    if (!this.particleGeometry || !this.particleContainer) return;

    const positions = this.particleGeometry.attributes.position.array as Float32Array;
    const sizes = this.particleGeometry.attributes.size.array as Float32Array;
    const colors = this.particleGeometry.attributes.color.array as Float32Array;

    const containerPos = this.particleContainer.position;

    // Pack active particles into the beginning of the buffer
    let bufferIndex = 0;
    for (const particle of this.particles) {
      if (!particle.active) continue;

      // Convert to local coordinates
      const localPos = particle.position.clone().sub(containerPos);
      positions[bufferIndex * 3] = localPos.x;
      positions[bufferIndex * 3 + 1] = localPos.y;
      positions[bufferIndex * 3 + 2] = localPos.z;

      // Calculate fade based on lifetime
      const lifeRatio = particle.life / particle.maxLife;
      const fade = Math.sin(lifeRatio * Math.PI); // Fade in and out

      sizes[bufferIndex] = particle.size * fade;

      // Sparkle color: green with pulsing brightness
      const pulse = 0.7 + 0.3 * Math.sin(performance.now() * 0.01 + bufferIndex);
      const brightness = particle.brightness * fade * pulse;

      // Green sparkle color (neon-green style)
      colors[bufferIndex * 3] = 0.5 * brightness; // R
      colors[bufferIndex * 3 + 1] = 1.0 * brightness; // G
      colors[bufferIndex * 3 + 2] = 0.4 * brightness; // B

      bufferIndex++;
    }

    // Hide remaining buffer slots
    for (let i = bufferIndex; i < this.MAX_PARTICLES; i++) {
      positions[i * 3] = 0;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = 0;
      sizes[i] = 0;
      colors[i * 3] = 0;
      colors[i * 3 + 1] = 0;
      colors[i * 3 + 2] = 0;
    }

    this.particleGeometry.attributes.position.needsUpdate = true;
    this.particleGeometry.attributes.size.needsUpdate = true;
    this.particleGeometry.attributes.color.needsUpdate = true;
  }
}
